import React, { useEffect, useMemo, useState } from "react";
import { Link2, Loader2, Plus } from "lucide-react";
import type {
  AdminPortalPermissionMatrix,
  AllotablePermissionsView,
  GroupedPermissionsView,
  Permission,
  Resource,
  RoleResource,
  RoleResourcePermission,
} from "../../../features/rbac/types";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { EmptyState } from "../shared/EmptyState";
import { FormModal, LabeledField, SelectInput } from "../shared/FormModal";
import { RowActions } from "../shared/RowActions";
import { SearchInput } from "../shared/SearchInput";
import { SectionCard } from "../shared/SectionCard";
import { StatusPill } from "../shared/StatusPill";
import { useRbacAction } from "../hooks/useRbacAction";
import {
  AdminPortalPermissionMatrixView,
  GroupedPermissionsListView,
} from "./PermissionMatrixView";

interface MappingsTabProps {
  mappings: RoleResourcePermission[];
  roleResources: RoleResource[];
  resources: Resource[];
  permissions: Permission[];
  permissionViews: {
    roleResourceId: number | null;
    grouped: GroupedPermissionsView | null;
    adminPortal: AdminPortalPermissionMatrix | null;
  };
  allotablePermissions: AllotablePermissionsView | null;
  viewsLoading: boolean;
  onCreate: (payload: { roleResourceId: number; resourceIds: number[]; permissionIds: number[] }) => void;
  onUpdate: (payload: {
    roleResourcePermissionId: number;
    granteeRoleResourceId: number;
    resourceId: number;
    permissionId: number;
  }) => void;
  onDelete: (id: number) => void;
  onFetchPermissionViews: (roleResourceId: number) => void;
  onFetchAllotable: (resourceId: number) => void;
}

interface CreateMappingForm {
  roleResourceId: number | "";
  resourceIds: number[];
  permissionIds: number[];
}

const emptyCreateForm: CreateMappingForm = { roleResourceId: "", resourceIds: [], permissionIds: [] };

interface EditMappingForm {
  granteeRoleResourceId: number | "";
  resourceId: number | "";
  permissionId: number | "";
}

const emptyEditForm: EditMappingForm = { granteeRoleResourceId: "", resourceId: "", permissionId: "" };

export const MappingsTab: React.FC<MappingsTabProps> = ({
  mappings,
  roleResources,
  resources,
  permissions,
  permissionViews,
  allotablePermissions,
  viewsLoading,
  onCreate,
  onUpdate,
  onDelete,
  onFetchPermissionViews,
  onFetchAllotable,
}) => {
  const { matches, isBusy } = useRbacAction();

  const [search, setSearch] = useState("");
  const [filterRR, setFilterRR] = useState<number | "">("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateMappingForm>(emptyCreateForm);

  const [editTarget, setEditTarget] = useState<RoleResourcePermission | null>(null);
  const [editForm, setEditForm] = useState<EditMappingForm>(emptyEditForm);

  const [deleteTarget, setDeleteTarget] = useState<RoleResourcePermission | null>(null);

  const [previewRR, setPreviewRR] = useState<number | "">("");
  const [previewResource, setPreviewResource] = useState<number | "">("");

  // Maintain default selections in sync with available data.
  useEffect(() => {
    if (previewRR === "" && roleResources[0]) setPreviewRR(roleResources[0].roleResourceId);
  }, [roleResources, previewRR]);
  useEffect(() => {
    if (previewResource === "" && resources[0]) setPreviewResource(resources[0].resourceId);
  }, [resources, previewResource]);

  const roleResourceLabel = (rrId: number) => {
    const rr = roleResources.find((r) => r.roleResourceId === rrId);
    if (!rr) return `RR #${rrId}`;
    return `${rr.role} (#${rrId})`;
  };
  const resourceLabel = (resourceId: number) => {
    const r = resources.find((res) => res.resourceId === resourceId);
    return r ? `${r.resourceName} (#${resourceId})` : `Resource #${resourceId}`;
  };
  const permissionLabel = (permissionId: number) => {
    const p = permissions.find((perm) => perm.permissionId === permissionId);
    return p ? `${p.action}:${p.scope} (#${permissionId})` : `Permission #${permissionId}`;
  };

  const filteredMappings = useMemo(() => {
    const q = search.trim().toLowerCase();
    return mappings.filter((m) => {
      if (filterRR !== "" && m.granteeRoleResourceId !== filterRR) return false;
      if (!q) return true;
      return (
        roleResourceLabel(m.granteeRoleResourceId).toLowerCase().includes(q) ||
        resourceLabel(m.resourceId).toLowerCase().includes(q) ||
        permissionLabel(m.permissionId).toLowerCase().includes(q) ||
        String(m.roleResourcePermissionId).includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappings, filterRR, search, roleResources, resources, permissions]);

  const openCreate = () => {
    setCreateForm({
      roleResourceId: roleResources[0]?.roleResourceId ?? "",
      resourceIds: [],
      permissionIds: [],
    });
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateForm(emptyCreateForm);
  };

  const handleToggle = (
    key: "resourceIds" | "permissionIds",
    id: number,
    checked: boolean
  ) => {
    setCreateForm((f) => {
      const current = new Set(f[key]);
      if (checked) current.add(id);
      else current.delete(id);
      return { ...f, [key]: Array.from(current).sort((a, b) => a - b) };
    });
  };

  const createDisabled =
    createForm.roleResourceId === "" ||
    createForm.resourceIds.length === 0 ||
    createForm.permissionIds.length === 0;

  const handleCreateSubmit = () => {
    if (createDisabled) return;
    onCreate({
      roleResourceId: createForm.roleResourceId as number,
      resourceIds: createForm.resourceIds,
      permissionIds: createForm.permissionIds,
    });
    closeCreate();
  };

  const openEdit = (mapping: RoleResourcePermission) => {
    setEditTarget(mapping);
    setEditForm({
      granteeRoleResourceId: mapping.granteeRoleResourceId,
      resourceId: mapping.resourceId,
      permissionId: mapping.permissionId,
    });
  };

  const closeEdit = () => {
    setEditTarget(null);
    setEditForm(emptyEditForm);
  };

  const editDisabled =
    editForm.granteeRoleResourceId === "" || editForm.resourceId === "" || editForm.permissionId === "";

  const handleEditSubmit = () => {
    if (!editTarget || editDisabled) return;
    onUpdate({
      roleResourcePermissionId: editTarget.roleResourcePermissionId,
      granteeRoleResourceId: editForm.granteeRoleResourceId as number,
      resourceId: editForm.resourceId as number,
      permissionId: editForm.permissionId as number,
    });
    closeEdit();
  };

  const handlePreviewClick = () => {
    if (previewRR !== "") onFetchPermissionViews(previewRR as number);
  };

  const handleAllotableClick = () => {
    if (previewResource !== "") onFetchAllotable(previewResource as number);
  };

  return (
    <div className="space-y-6">
      <SectionCard
        title="Role-resource ↔ permission mappings"
        description="Each row is a single (role-resource, resource, permission) triple."
        actions={
          <>
            <SearchInput value={search} onChange={setSearch} placeholder="Search mappings" />
            <SelectInput
              value={filterRR}
              onChange={(e) => setFilterRR(e.target.value === "" ? "" : Number(e.target.value))}
            >
              <option value="">All role-resources</option>
              {roleResources.map((rr) => (
                <option key={rr.roleResourceId} value={rr.roleResourceId}>
                  {rr.role} (#{rr.roleResourceId})
                </option>
              ))}
            </SelectInput>
            <button
              type="button"
              onClick={openCreate}
              disabled={isBusy || roleResources.length === 0 || resources.length === 0 || permissions.length === 0}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
              title={
                roleResources.length === 0 || resources.length === 0 || permissions.length === 0
                  ? "Need at least one role-resource, resource and permission"
                  : ""
              }
            >
              <Plus className="h-4 w-4" />
              New mapping
            </button>
          </>
        }
      >
        {filteredMappings.length === 0 ? (
          <EmptyState
            icon={<Link2 className="h-5 w-5" />}
            title={search || filterRR !== "" ? "No mappings match your filter" : "No mappings yet"}
            description={
              search || filterRR !== ""
                ? "Try clearing the filters or pick another role-resource."
                : "Grant resources and permissions to role-resources to make access work."
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-cardborder bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-2">Mapping ID</th>
                  <th className="px-4 py-2">Role-resource</th>
                  <th className="px-4 py-2">Resource</th>
                  <th className="px-4 py-2">Permission</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMappings.map((mapping) => (
                  <tr
                    key={mapping.roleResourcePermissionId}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">
                      {mapping.roleResourcePermissionId}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill variant="info">
                        {roleResourceLabel(mapping.granteeRoleResourceId)}
                      </StatusPill>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{resourceLabel(mapping.resourceId)}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {permissionLabel(mapping.permissionId)}
                    </td>
                    <td className="px-4 py-3">
                      <RowActions
                        onEdit={() => openEdit(mapping)}
                        onDelete={() => setDeleteTarget(mapping)}
                        editing={matches(
                          "updateRoleResourcePermission",
                          mapping.roleResourcePermissionId
                        )}
                        deleting={matches(
                          "deleteRoleResourcePermission",
                          mapping.roleResourcePermissionId
                        )}
                        disabled={isBusy}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard
          title="Admin-portal permission matrix"
          description="GET /authorization/users/adminportal/permissions/:roleResourceId"
          actions={
            <>
              <SelectInput
                value={previewRR}
                onChange={(e) =>
                  setPreviewRR(e.target.value === "" ? "" : Number(e.target.value))
                }
              >
                <option value="">Select role-resource</option>
                {roleResources.map((rr) => (
                  <option key={rr.roleResourceId} value={rr.roleResourceId}>
                    {rr.role} (#{rr.roleResourceId})
                  </option>
                ))}
              </SelectInput>
              <button
                type="button"
                onClick={handlePreviewClick}
                disabled={previewRR === "" || viewsLoading}
                className="inline-flex items-center gap-2 rounded-md border border-cardborder bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {viewsLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Load
              </button>
            </>
          }
        >
          {permissionViews.adminPortal ? (
            <AdminPortalPermissionMatrixView
              matrix={permissionViews.adminPortal}
              resources={resources}
            />
          ) : (
            <EmptyState
              title="No permission matrix loaded"
              description="Select a role-resource and click Load to view its admin-portal permissions."
            />
          )}
        </SectionCard>

        <SectionCard
          title="Grouped permissions"
          description="GET /authorization/users/permissions/:roleResourceId"
        >
          {permissionViews.grouped ? (
            <GroupedPermissionsListView grouped={permissionViews.grouped} />
          ) : (
            <EmptyState
              title="No grouped view loaded"
              description="Will be loaded together with the admin-portal matrix above."
            />
          )}
        </SectionCard>
      </div>

      <SectionCard
        title="Allotable permissions (current user context)"
        description="GET /authorization/role-resource-permissions/:resourceId/allotable"
        actions={
          <>
            <SelectInput
              value={previewResource}
              onChange={(e) =>
                setPreviewResource(e.target.value === "" ? "" : Number(e.target.value))
              }
            >
              <option value="">Select resource</option>
              {resources.map((r) => (
                <option key={r.resourceId} value={r.resourceId}>
                  {r.resourceName} (#{r.resourceId})
                </option>
              ))}
            </SelectInput>
            <button
              type="button"
              onClick={handleAllotableClick}
              disabled={previewResource === "" || viewsLoading}
              className="inline-flex items-center gap-2 rounded-md border border-cardborder bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {viewsLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Load
            </button>
          </>
        }
      >
        {allotablePermissions && Object.keys(allotablePermissions).length > 0 ? (
          <div className="space-y-3">
            {Object.entries(allotablePermissions).map(([resourceId, value]) => (
              <div key={resourceId} className="rounded-md border border-cardborder">
                <div className="border-b border-cardborder bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-600">
                  {value.resourceName} <span className="text-gray-400">#{resourceId}</span>
                </div>
                <div className="flex flex-wrap gap-2 px-3 py-3">
                  {value.permissions.map((perm) => (
                    <StatusPill key={perm.permissionId} variant="info">
                      {perm.action}:{perm.scope} (#{perm.permissionId})
                    </StatusPill>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No allotable permissions loaded"
            description="Select a resource and click Load. The path id is informational only."
          />
        )}
      </SectionCard>

      <FormModal
        open={createOpen}
        title="Create role-resource permission mapping"
        onClose={closeCreate}
        onSubmit={handleCreateSubmit}
        submitting={matches("createRoleResourcePermission")}
        submitDisabled={createDisabled}
        submitLabel={`Create ${
          createForm.resourceIds.length * createForm.permissionIds.length || ""
        } mapping(s)`}
        width="w-full max-w-3xl"
      >
        <LabeledField
          label="Role-resource (grantee)"
          required
          hint="Mappings will be created against this role-resource."
        >
          <SelectInput
            value={createForm.roleResourceId}
            onChange={(e) =>
              setCreateForm((f) => ({
                ...f,
                roleResourceId: e.target.value === "" ? "" : Number(e.target.value),
              }))
            }
          >
            <option value="" disabled>
              Select role-resource
            </option>
            {roleResources.map((rr) => (
              <option key={rr.roleResourceId} value={rr.roleResourceId}>
                {rr.role} (#{rr.roleResourceId})
              </option>
            ))}
          </SelectInput>
        </LabeledField>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <LabeledField label="Resources" required hint="Select one or more">
            <div className="max-h-56 overflow-auto rounded-md border border-cardborder">
              {resources.map((r) => (
                <label
                  key={r.resourceId}
                  className="flex cursor-pointer items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-0 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={createForm.resourceIds.includes(r.resourceId)}
                    onChange={(e) => handleToggle("resourceIds", r.resourceId, e.target.checked)}
                  />
                  <span>
                    {r.resourceName} <span className="text-xs text-gray-400">#{r.resourceId}</span>
                  </span>
                </label>
              ))}
            </div>
          </LabeledField>
          <LabeledField label="Permissions" required hint="Select one or more">
            <div className="max-h-56 overflow-auto rounded-md border border-cardborder">
              {permissions.map((p) => (
                <label
                  key={p.permissionId}
                  className="flex cursor-pointer items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-0 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={createForm.permissionIds.includes(p.permissionId)}
                    onChange={(e) => handleToggle("permissionIds", p.permissionId, e.target.checked)}
                  />
                  <span>
                    {p.action}:{p.scope}{" "}
                    <span className="text-xs text-gray-400">#{p.permissionId}</span>
                  </span>
                </label>
              ))}
            </div>
          </LabeledField>
        </div>

        <p className="text-xs text-gray-500">
          The API creates the Cartesian product: {createForm.resourceIds.length || 0} ×{" "}
          {createForm.permissionIds.length || 0} ={" "}
          <span className="font-semibold text-gray-700">
            {createForm.resourceIds.length * createForm.permissionIds.length}
          </span>{" "}
          mapping(s). Existing pairs are not duplicated.
        </p>
      </FormModal>

      <FormModal
        open={editTarget != null}
        title={`Edit mapping #${editTarget?.roleResourcePermissionId ?? ""}`}
        onClose={closeEdit}
        onSubmit={handleEditSubmit}
        submitting={
          editTarget
            ? matches("updateRoleResourcePermission", editTarget.roleResourcePermissionId)
            : false
        }
        submitDisabled={editDisabled}
        submitLabel="Save changes"
      >
        <LabeledField label="Role-resource (grantee)" required>
          <SelectInput
            value={editForm.granteeRoleResourceId}
            onChange={(e) =>
              setEditForm((f) => ({
                ...f,
                granteeRoleResourceId: e.target.value === "" ? "" : Number(e.target.value),
              }))
            }
          >
            <option value="" disabled>
              Select role-resource
            </option>
            {roleResources.map((rr) => (
              <option key={rr.roleResourceId} value={rr.roleResourceId}>
                {rr.role} (#{rr.roleResourceId})
              </option>
            ))}
          </SelectInput>
        </LabeledField>
        <LabeledField label="Resource" required>
          <SelectInput
            value={editForm.resourceId}
            onChange={(e) =>
              setEditForm((f) => ({
                ...f,
                resourceId: e.target.value === "" ? "" : Number(e.target.value),
              }))
            }
          >
            <option value="" disabled>
              Select resource
            </option>
            {resources.map((r) => (
              <option key={r.resourceId} value={r.resourceId}>
                {r.resourceName} (#{r.resourceId})
              </option>
            ))}
          </SelectInput>
        </LabeledField>
        <LabeledField label="Permission" required>
          <SelectInput
            value={editForm.permissionId}
            onChange={(e) =>
              setEditForm((f) => ({
                ...f,
                permissionId: e.target.value === "" ? "" : Number(e.target.value),
              }))
            }
          >
            <option value="" disabled>
              Select permission
            </option>
            {permissions.map((p) => (
              <option key={p.permissionId} value={p.permissionId}>
                {p.action}:{p.scope} (#{p.permissionId})
              </option>
            ))}
          </SelectInput>
        </LabeledField>
      </FormModal>

      <ConfirmDialog
        open={deleteTarget != null}
        title={`Delete mapping #${deleteTarget?.roleResourcePermissionId ?? ""}?`}
        description={
          deleteTarget ? (
            <>
              Mapping for{" "}
              <strong>{roleResourceLabel(deleteTarget.granteeRoleResourceId)}</strong> →{" "}
              <strong>{resourceLabel(deleteTarget.resourceId)}</strong> ·{" "}
              <strong>{permissionLabel(deleteTarget.permissionId)}</strong>
            </>
          ) : (
            ""
          )
        }
        confirmLabel="Delete mapping"
        destructive
        loading={
          deleteTarget
            ? matches("deleteRoleResourcePermission", deleteTarget.roleResourcePermissionId)
            : false
        }
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            onDelete(deleteTarget.roleResourcePermissionId);
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
};
